import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  Text,
  FlatList,
  TouchableOpacity,
  StyleSheet,
  Image,
  ScrollView,
} from 'react-native';
import {
  Card,
  Button,
  Title,
  Paragraph,
  Chip,
} from 'react-native-paper';
import { Ionicons } from '@expo/vector-icons';
import apiService from '../services/ApiService';
import { useTheme } from '../contexts/ThemeContext';
import { ThemedContainer, ThemedText } from '../components/ThemedContainer';
import BarsFilterPanel from '../components/BarsFilterPanel';

interface Location {
  id: number;
  name: string;
  venue_type: string;
  description: string;
  website?: string;
  phone?: string;
  email?: string;
  instagram?: string;
  facebook?: string;
  opentable_url?: string;
  resy_url?: string;
  google_maps_url?: string;
  map_shortlink?: string;
  price_tier?: string;
  opened_year?: number;
  is_active?: boolean;
  latitude?: number;
  longitude?: number;
  address: string;
  address2?: string;
  city: string;
  region: string;
  postal_code?: string;
  country: string;
  rating_avg: string | number;
  rating_count: number;
  rum_selection_score?: number;
  cocktail_program_score?: number;
  na_program_depth?: number;
  last_updated_utc?: string;
  amenities?: string[];
  program_tags?: string[];
  hero_image?: string;
}

const BarsScreen: React.FC<{ navigation: any }> = ({ navigation }) => {
  const { themeData } = useTheme();
  const [loading, setLoading] = useState(true);
  const [locations, setLocations] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filterPanelVisible, setFilterPanelVisible] = useState(false);
  const [venueTypes, setVenueTypes] = useState<string[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [priceTiers, setPriceTiers] = useState<string[]>([]);
  
  // Default filters - default to Florida
  const [filters, setFilters] = useState({
    search: '',
    region: 'Florida',
    venueType: '',
    minRating: 0,
    priceTier: '',
    hasWebsite: false,
    hasPhone: false,
  });

  useEffect(() => {
    loadLocations();
    loadFilterOptions();
  }, []);

  const loadFilterOptions = async () => {
    try {
      // Load venue types
      const types = await apiService.getVenueTypes();
      setVenueTypes(types);
      
      // Extract unique regions and price tiers from loaded locations
      // This will be updated after locations are loaded
    } catch (error) {
      console.error('Error loading filter options:', error);
    }
  };

  const loadLocations = async (currentFilters = filters) => {
    try {
      setLoading(true);
      
      // Convert frontend filters to API filters
      const apiFilters: any = {};
      if (currentFilters.search) apiFilters.search = currentFilters.search;
      if (currentFilters.region) apiFilters.region = currentFilters.region;
      if (currentFilters.venueType) apiFilters.venue_type = currentFilters.venueType;
      if (currentFilters.minRating > 0) apiFilters.min_rating = currentFilters.minRating;
      if (currentFilters.priceTier) apiFilters.price_tier = currentFilters.priceTier;
      if (currentFilters.hasWebsite) apiFilters.has_website = true;
      if (currentFilters.hasPhone) apiFilters.has_phone = true;
      
      const locationsData = await apiService.getVenues(apiFilters);
      setLocations(locationsData);
      
      // Extract unique regions and price tiers for filters (only on first load)
      if (locations.length === 0) {
        const allLocationsData = await apiService.getVenues();
        const uniqueRegions = [...new Set(allLocationsData.map(loc => loc.region).filter(Boolean))].sort();
        const uniquePriceTiers = [...new Set(allLocationsData.map(loc => loc.price_tier).filter(Boolean))].sort();
        
        setRegions(uniqueRegions);
        setPriceTiers(uniquePriceTiers);
      }
    } catch (err) {
      setError('Failed to load locations');
      console.error('Error loading locations:', err);
    } finally {
      setLoading(false);
    }
  };



  const handleFiltersChange = (newFilters: any) => {
    setFilters(newFilters);
    // Reload data with new filters
    loadLocations(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      region: 'Florida', // Keep Florida as default
      venueType: '',
      minRating: 0,
      priceTier: '',
      hasWebsite: false,
      hasPhone: false,
    });
  };



  const renderLocation = ({ item }: { item: Location }) => {
    // Skip inactive locations (default to true if not specified)
    if (item.is_active === false) {
      return null;
    }

    // Get first two lines of description (shorter for more compact cards)
    const getShortDescription = (description: string) => {
      if (!description) return '';
      const lines = description.split('\n').filter(line => line.trim().length > 0);
      const firstTwoLines = lines.slice(0, 2).join('\n');
      // If description is longer than 150 characters, truncate it
      return firstTwoLines.length > 150 ? firstTwoLines.substring(0, 150) + '...' : firstTwoLines;
    };

    return (
      <TouchableOpacity
        onPress={() => navigation.navigate('VenueDetail', { venueId: item.id })}
        style={styles.cardTouchable}
      >
        <Card style={[
          styles.locationCard, 
          { backgroundColor: themeData.colors.cardBackground },
          (typeof item.rating_avg === 'string' ? parseFloat(item.rating_avg) : item.rating_avg) >= 4.5 && styles.featuredCard
        ]}>
          <Card.Content style={styles.blogContent}>
            <View style={styles.locationHeader}>
              <Title style={[styles.locationName, { color: themeData.colors.text }]}>{item.name}</Title>
              <View style={styles.ratingContainer}>
                <Ionicons name="star" size={16} color={themeData.colors.highlight1} />
                <Text style={[styles.rating, { color: themeData.colors.text }]}>
                  {item.rating_avg ? (typeof item.rating_avg === 'string' ? parseFloat(item.rating_avg).toFixed(1) : item.rating_avg.toFixed(1)) : 'N/A'}
                </Text>
              </View>
            </View>
            
            <Paragraph style={[styles.locationAddress, { color: themeData.colors.textSecondary }]}>
              {item.city}, {item.region}
            </Paragraph>
            
            <Paragraph style={[styles.locationDescription, { color: themeData.colors.text }]}>
              {getShortDescription(item.description || '')}
            </Paragraph>
          </Card.Content>
        </Card>
      </TouchableOpacity>
    );
  };

  if (loading) {
    return (
      <ThemedContainer style={styles.container}>
        <View style={styles.loadingContainer}>
          <Ionicons name="refresh" size={48} color={themeData.colors.primary} />
          <ThemedText style={styles.loadingText}>Loading tiki bars...</ThemedText>
        </View>
      </ThemedContainer>
    );
  }

  if (error) {
    return (
      <ThemedContainer style={styles.container}>
        <View style={styles.errorContainer}>
          <Ionicons name="alert-circle" size={48} color={themeData.colors.error} />
          <ThemedText style={styles.errorText}>{error}</ThemedText>
          <Button mode="contained" onPress={loadLocations}>
            Try Again
          </Button>
        </View>
      </ThemedContainer>
    );
  }

  // Filter locations by type (server-side filtering handles the rest)
  const tikiBars = locations.filter(loc => 
    loc.venue_type === 'tiki_bar'
  );
  const otherBars = locations.filter(loc => 
    loc.venue_type === 'craft_cocktail_bar' || 
    loc.venue_type === 'lounge' ||
    loc.venue_type === 'bar'
  );

  return (
    <ThemedContainer style={styles.container}>
      <ScrollView>
        <View style={styles.header}>
          <Title style={styles.headerTitle}>🌺 Tiki Bars & Rum Venues</Title>
          <Paragraph style={styles.headerSubtitle}>
            Discover authentic tiki bars and venues with great tiki cocktails in the Tampa Bay area
          </Paragraph>
        </View>
        
        {/* Filter Panel */}
        <BarsFilterPanel
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onClearFilters={handleClearFilters}
          venueTypes={venueTypes}
          regions={regions}
          priceTiers={priceTiers}
          isVisible={filterPanelVisible}
          onToggleVisibility={() => setFilterPanelVisible(!filterPanelVisible)}
        />
        
        {/* Results Summary */}
        <View style={styles.resultsSummary}>
          <Paragraph style={[styles.resultsText, { color: themeData.colors.textSecondary }]}>
            Showing {locations.length} venues
            {filters.region && ` in ${filters.region}`}
            {filters.venueType && ` (${filters.venueType.replace('_', ' ')})`}
          </Paragraph>
        </View>
        
        {tikiBars.length > 0 && (
          <View style={styles.section}>
            <Title style={styles.sectionTitle}>🌺 Authentic Tiki Bars</Title>
            <Paragraph style={styles.sectionSubtitle}>
              True tiki bars with Polynesian decor, carved masks, and immersive tropical atmosphere
            </Paragraph>
                         <FlatList
               data={tikiBars}
               renderItem={renderLocation}
               keyExtractor={(item) => item.id.toString()}
               contentContainerStyle={styles.listContainer}
               showsVerticalScrollIndicator={false}
               scrollEnabled={false}
             />
          </View>
        )}
        
        {otherBars.length > 0 && (
          <View style={styles.section}>
            <Title style={styles.sectionTitle}>🍹 Bars with Tiki Cocktails</Title>
            <Paragraph style={styles.sectionSubtitle}>
              Great bars that serve excellent tiki cocktails and tropical drinks
            </Paragraph>
                         <FlatList
               data={otherBars}
               renderItem={renderLocation}
               keyExtractor={(item) => item.id.toString()}
               contentContainerStyle={styles.listContainer}
               showsVerticalScrollIndicator={false}
               scrollEnabled={false}
             />
          </View>
        )}
      </ScrollView>
    </ThemedContainer>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    padding: 24,
    alignItems: 'center',
    marginBottom: 16,
  },
  headerTitle: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 8,
  },
  headerSubtitle: {
    fontSize: 16,
    textAlign: 'center',
    opacity: 0.8,
  },
  section: {
    marginBottom: 24,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    marginBottom: 8,
    textAlign: 'center',
    paddingHorizontal: 16,
  },
  sectionSubtitle: {
    fontSize: 14,
    textAlign: 'center',
    opacity: 0.7,
    marginBottom: 16,
    paddingHorizontal: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  loadingText: {
    fontSize: 18,
    marginTop: 16,
    textAlign: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorText: {
    fontSize: 18,
    marginTop: 16,
    marginBottom: 24,
    textAlign: 'center',
  },
  listContainer: {
    padding: 16,
  },
  locationCard: {
    marginBottom: 12,
    borderRadius: 8,
    elevation: 2,
  },
  featuredCard: {
    elevation: 8,
    borderWidth: 2,
  },
  blogContent: {
    padding: 12,
  },
  locationHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  locationName: {
    fontSize: 16,
    fontWeight: 'bold',
    flex: 1,
  },
  ratingContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginLeft: 8,
  },
  rating: {
    fontSize: 16,
    fontWeight: 'bold',
    marginLeft: 4,
  },


  locationAddress: {
    fontSize: 13,
    marginBottom: 6,
  },
  locationDescription: {
    fontSize: 12,
    lineHeight: 16,
    marginBottom: 6,
  },

  cardTouchable: {
    marginBottom: 8,
  },
  resultsSummary: {
    paddingHorizontal: 16,
    paddingVertical: 8,
  },
  resultsText: {
    fontSize: 14,
    textAlign: 'center',
  },
  });
  
  export default BarsScreen; 